import type { EvidenceLibraryLimits } from '../../request/evidence-library/evidenceLibraryRequests.ts';

/**
 * Reads files from a folder picker or a drag-and-drop and decides, before anything is uploaded, which files go
 * and which are skipped. The same rules run again on the server; checking here lets the preview show them.
 */

export type PickedFile = {
  /** Stable id for this pick (path + size + modified time). */
  id: string;
  file: File;
  /** Path inside the picked folder, with the file name: "PBC/CTL_000012/a.pdf". The file name alone for loose files. */
  path: string;
  /** Why the file will not be uploaded, or null when it will. */
  skipReason: string | null;
};

const JUNK_NAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

export function isJunkFile(name: string): boolean {
  const lower = name.toLowerCase();
  return JUNK_NAMES.has(lower) || name.startsWith('~$') || name.startsWith('.');
}

export function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(i).toLowerCase() : '';
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** Folder depth of a path, not counting the file itself. */
export function folderDepth(path: string): number {
  return Math.max(0, path.split('/').filter(Boolean).length - 1);
}

export function skipReasonFor(file: { name: string; size: number }, path: string, limits: EvidenceLibraryLimits): string | null {
  if (isJunkFile(file.name)) return 'System or hidden file';
  if (path.split('/').some((seg) => seg.startsWith('.') && seg.length > 1)) return 'Inside a hidden folder';
  if (file.size <= 0) return 'Empty file';
  if (file.size > limits.max_file_bytes) return `Larger than ${formatBytes(limits.max_file_bytes)}`;
  const ext = extensionOf(file.name);
  if (!limits.allowed_extensions.includes(ext)) return ext ? `Unsupported type ${ext}` : 'No file type';
  if (folderDepth(path) > limits.max_folder_depth) return `Nested deeper than ${limits.max_folder_depth} folders`;
  return null;
}

function pickId(path: string, f: File): string {
  return `${path}::${f.size}::${f.lastModified}`;
}

export function toPickedFiles(entries: { file: File; path: string }[], limits: EvidenceLibraryLimits): PickedFile[] {
  const seen = new Set<string>();
  const out: PickedFile[] = [];
  for (const { file, path } of entries) {
    const clean = path.replace(/\\/g, '/').replace(/^\/+/, '') || file.name;
    const id = pickId(clean, file);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, file, path: clean, skipReason: skipReasonFor(file, clean, limits) });
  }
  return out.sort((a, b) => a.path.localeCompare(b.path));
}

/** Files from <input type="file" webkitdirectory> (webkitRelativePath set) or a plain multi-select. */
export function entriesFromInput(list: FileList | null): { file: File; path: string }[] {
  if (!list) return [];
  return Array.from(list).map((file) => ({ file, path: (file as any).webkitRelativePath || file.name }));
}

/** Files from a drop, walking dropped folders. Falls back to the flat file list where entries are unsupported. */
export async function entriesFromDrop(dt: DataTransfer): Promise<{ file: File; path: string }[]> {
  const items = Array.from(dt.items || []);
  const roots = items.map((it) => (typeof (it as any).webkitGetAsEntry === 'function' ? (it as any).webkitGetAsEntry() : null));
  if (!roots.some(Boolean)) {
    return Array.from(dt.files || []).map((file) => ({ file, path: file.name }));
  }
  const out: { file: File; path: string }[] = [];
  const walk = async (entry: any, prefix: string): Promise<void> => {
    if (!entry) return;
    if (entry.isFile) {
      const file: File = await new Promise((resolve, reject) => entry.file(resolve, reject));
      out.push({ file, path: prefix + file.name });
      return;
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      // readEntries returns at most ~100 entries per call; read until it returns none.
      for (;;) {
        const batch: any[] = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        if (!batch.length) break;
        for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
      }
    }
  };
  for (const root of roots) await walk(root, '');
  return out;
}

export type FolderNode = {
  name: string;
  path: string;
  folders: FolderNode[];
  files: PickedFile[];
  /** Totals for everything under this folder. */
  fileCount: number;
  bytes: number;
};

/** Builds the folder tree shown in the preview. */
export function buildTree(files: PickedFile[]): FolderNode {
  const root: FolderNode = { name: '', path: '', folders: [], files: [], fileCount: 0, bytes: 0 };
  for (const f of files) {
    const parts = f.path.split('/').filter(Boolean);
    let node = root;
    node.fileCount += 1;
    node.bytes += f.file.size;
    for (let i = 0; i < parts.length - 1; i++) {
      const path = parts.slice(0, i + 1).join('/');
      let child = node.folders.find((n) => n.path === path);
      if (!child) {
        child = { name: parts[i], path, folders: [], files: [], fileCount: 0, bytes: 0 };
        node.folders.push(child);
      }
      child.fileCount += 1;
      child.bytes += f.file.size;
      node = child;
    }
    node.files.push(f);
  }
  return root;
}

export type SelectionSummary = { count: number; bytes: number; overFiles: boolean; overBytes: boolean };

export function summarizeSelection(files: PickedFile[], excluded: Set<string>, limits: EvidenceLibraryLimits): SelectionSummary {
  let count = 0;
  let bytes = 0;
  for (const f of files) {
    if (f.skipReason || excluded.has(f.id)) continue;
    count += 1;
    bytes += f.file.size;
  }
  return { count, bytes, overFiles: count > limits.max_files_per_upload, overBytes: bytes > limits.max_bytes_per_upload };
}

export async function sha256Hex(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
